import { useSyncExternalStore } from 'react'

/**
 * Tiny theme store — single source of truth for the `data-theme` attribute.
 *
 * Preference (`light | dark | system`) persists to localStorage `it.theme`;
 * the *resolved* theme (`light | dark`) is what gets written to
 * `<html data-theme>`. The pre-paint script in index.html reads the same
 * storage key so first paint matches.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'it.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

const listeners = new Set<() => void>()
let preference: ThemePreference | null = null
let systemWatcherStarted = false

function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch {
    // Storage unavailable (private mode) — fall through to system.
  }
  return 'system'
}

function currentPreference(): ThemePreference {
  if (preference === null) preference = readStoredPreference()
  return preference
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light'
  }
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function applyToDocument(): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = getResolvedTheme()
}

function notify(): void {
  listeners.forEach((listener) => listener())
}

export function getThemePreference(): ThemePreference {
  return currentPreference()
}

export function getResolvedTheme(): ResolvedTheme {
  const pref = currentPreference()
  return pref === 'system' ? systemTheme() : pref
}

export function setThemePreference(next: ThemePreference): void {
  preference = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Persisting is best-effort; the in-memory value still applies.
  }
  applyToDocument()
  notify()
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Apply the stored theme and start watching OS-level changes (which only
 * matter while preference is `system`). Idempotent — safe to call once at
 * boot from main.tsx.
 */
export function initTheme(): void {
  applyToDocument()
  if (
    systemWatcherStarted ||
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return
  }
  systemWatcherStarted = true
  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    if (currentPreference() === 'system') {
      applyToDocument()
      notify()
    }
  })
}

/** React binding — re-renders on preference or OS theme changes. */
export function useTheme(): {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
} {
  const resolved = useSyncExternalStore(subscribeTheme, getResolvedTheme)
  const pref = useSyncExternalStore(subscribeTheme, getThemePreference)
  return { preference: pref, resolved, setPreference: setThemePreference }
}
